import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { fetchWrapper } from '../../helpers'
import {
    Box,
    Flex,
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    Spinner,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    Divider
} from '@chakra-ui/react'
import { InputText } from '../../components/form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';

const TpsChannel = ({ id, rs }) => {
    const [listChannel, setListChannel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [channelTpsData, setChannelTpsData] = useState([]); 
    const [isDataReady, setIsDataReady] = useState(false);

    // Parse existing TPS data
    const existingTpsData = rs?.tps ? JSON.parse(rs.tps) : [];

    const FormSchema = Yup.object().shape({
        tps: Yup.array().of(
            Yup.object().shape({
                otp_trans: Yup.number()
                    .typeError('Must be a number')
                    .nullable()
                    .transform((value, originalValue) => {
                        return originalValue === '' ? null : value;
                    }),
                marketing: Yup.number()
                    .typeError('Must be a number')
                    .nullable()
                    .transform((value, originalValue) => {
                        return originalValue === '' ? null : value;
                    }),
                value: Yup.number()
                    .typeError('Must be a number')
                    .nullable()
                    .transform((value, originalValue) => {
                        return originalValue === '' ? null : value;
                    }),
            })
        )
    });

    const [initialValues, setInitialValues] = useState({ tps: [] });

    useEffect(() => {
        loadChannelAndTpsData();
    }, [id, rs?.tps]);

    const loadChannelAndTpsData = async () => {
        setLoading(true);
        setIsDataReady(false);

        try {
           
            const url = `${process.env.REACT_APP_API_URL}/channel/list?customer_id=${id}`;
            const data = await fetchWrapper.get(url);
            const channels = data?.data || [];

            console.log("Loaded channels:", channels);
            setListChannel(channels);

            const combinedData = channels.map((channel) => {
                const existingTps = existingTpsData.find(item => item.channel_id === channel.id);
                console.log(`Channel ${channel.id} (${channel.name}) - Found TPS:`, existingTps);

                return {
                    ...channel,
                    tpsData: existingTps || null
                };
            });

       
            setChannelTpsData(combinedData);

            // Build form initial values
            const formInitialValues = {
                tps: combinedData.map((channel) => {
                    const existingTps = channel.tpsData;

                    if (channel.id === 1) {
                        // SMS channel - handle otp_trans and marketing
                        const result = {
                            channel_id: channel.id,
                            otp_trans: existingTps?.otp_trans || existingTps?.tps || '',
                            marketing: existingTps?.marketing || ''
                        };
                        console.log(`SMS channel initial values:`, result);
                        return result;
                    } else {
                        // Other channels - single TPS value
                        const result = {
                            channel_id: channel.id,
                            value: existingTps?.tps || ''
                        };
                        console.log(`Channel ${channel.name} initial values:`, result);
                        return result;
                    }
                })
            };

            console.log("Final form initial values:", formInitialValues);
            setInitialValues(formInitialValues);
            setIsDataReady(true);

        } catch (error) {
            console.error("Error loading channels:", error);
            toast(`Error loading channels`, { type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (values, { setSubmitting }) => {
        try {
            const postData = values.tps.map((tpsItem, index) => {
                const channel = listChannel[index];

                if (channel.id === 1) {
                    // SMS channel - return with otp_trans and marketing
                    return {
                        channel_id: channel.id,
                        otp_trans: Number(tpsItem.otp_trans) || 0,
                        marketing: Number(tpsItem.marketing) || 0
                    };
                } else {
                    // Other channels - return with single tps value
                    return {
                        channel_id: channel.id,
                        tps: Number(tpsItem.value) || 0
                    };
                }
            });

            console.log('Submitting data:', postData);

            const url = `${process.env.REACT_APP_API_URL}/customer/update-tps`;
            await fetchWrapper.post(url, {
                tps: JSON.stringify(postData),
                id: id
            });

            toast(`TPS settings have been saved successfully`, { type: "success" });
        } catch (err) {
            console.error(err);
            toast(`Error saving TPS settings`, { type: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    // Don't render the form until we have all data ready
    if (loading || !isDataReady) {
        return (
            <Box>
                <Box mb={3}>
                    <Heading size="sm" mb={1}>Channel TPS Configuration</Heading>
                    <Text color="gray.600" fontSize="xs">
                        Configure Transactions Per Second (TPS) limits for each communication channel
                    </Text>
                </Box>

                <Flex justify="center" align="center" py={6}>
                    <VStack spacing={2}>
                        <Spinner size="md" color="blue.500" />
                        <Text color="gray.500" fontSize="sm">Loading channels...</Text>
                    </VStack>
                </Flex>
            </Box>
        );
    }

    return (
        <Box>
            <Box mb={3}>
                <Heading size="sm" mb={1}>Channel TPS Configuration</Heading>
                <Text color="gray.600" fontSize="xs">
                    Configure Transactions Per Second (TPS) limits for each communication channel
                </Text>
            </Box>

            <Formik
                    enableReinitialize={true}
                    validationSchema={FormSchema}
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                >
                    {({
                        setFieldValue,
                        values,
                        errors,
                        touched,
                        isSubmitting,
                    }) => {

                        return (
                            <Form>
                                <VStack spacing={3} align="stretch">
                                    <TableContainer>
                                        <Table variant='simple' size="md">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th width="200px" fontSize="sm" fontWeight="semibold">
                                                        Channel
                                                    </Th>
                                                    <Th fontSize="sm" fontWeight="semibold" textAlign="right">
                                                        TPS Configuration
                                                    </Th>
                                                </Tr>
                                            </Thead>

                                            <Tbody>
                                                {channelTpsData?.map((channel, index) => (
                                                    <Tr key={channel?.id || index} _hover={{ bg: "gray.50" }}>
                                                        <Td>
                                                            <VStack align="start" spacing={2}>
                                                                <HStack>
                                                                    <Text fontWeight="medium">{channel.name}</Text>

                                                                </HStack>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <Flex justify="flex-end">
                                                                {channel.id === 1 ? (
                                                               
                                                                    <HStack spacing={4} align="start">
                                                                        <Box>
                                                                            <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700" textAlign="start">
                                                                                OTP Transactions
                                                                            </Text>
                                                                            <InputText
                                                                                name={`tps.${index}.otp_trans`}
                                                                                title={''}
                                                                                placeholder="OTP TPS"
                                                                                size="sm"
                                                                                width="120px"
                                                                            />
                                                                        </Box>
                                                                        <Box>
                                                                            <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700" textAlign="start">
                                                                                Marketing Transactions
                                                                            </Text>
                                                                            <InputText
                                                                                name={`tps.${index}.marketing`}
                                                                                title={''}
                                                                                placeholder="Marketing TPS"
                                                                                size="sm"
                                                                                width="120px"
                                                                            />
                                                                        </Box>
                                                                    </HStack>
                                                                ) : (
                                                                    
                                                                    <Box>
                                                                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700" textAlign="start">
                                                                            TPS
                                                                        </Text>
                                                                        <InputText
                                                                            name={`tps.${index}.value`}
                                                                            title={''}
                                                                            placeholder="Enter TPS limit"
                                                                            size="sm"
                                                                            width="150px"
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Flex>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>

                                    <Divider />

                                    <Flex justify="flex-end">
                                        <Button
                                            type="submit"
                                            colorScheme="blue"
                                            isLoading={isSubmitting}
                                            loadingText="Saving..."
                                            size="sm"
                                            px={6}
                                        >
                                            Save Configuration
                                        </Button>
                                    </Flex>
                                </VStack>
                            </Form>
                        );
                    }}
                </Formik>
        </Box>
    );
};

export default TpsChannel;